import React from "react";
import { TextField, InputAdornment } from "@mui/material";

const MuiInput = ({
  value,
  onChange,
  type = "text",
  placeholder,
  label = "",
  errorMessage = "",
  disable = false,
  mt = "10px",
  editBoolean,
  maxLength,
  allowDecimal = true,
  maxDecimals = null,
  backgroundColor = "#fff"
}) => {
  const handleChange = (event) => {
    const inputValue = event.target.value;
    if (type === "number") {
      // Define regex based on allowDecimal prop
      const regex = allowDecimal ? /[^0-9.]/g : /[^0-9]/g;
      // Remove any non-numeric characters (and decimal point if not allowed)
      const numericValue = inputValue.replace(regex, '');
      
      let sanitizedValue = numericValue;
      if (allowDecimal) {
        // Handle decimal point only if decimals are allowed
        const parts = numericValue.split('.');
        // Cap decimal places if maxDecimals is specified
        const decimalPart = parts.length > 1
          ? '.' + (maxDecimals !== null ? parts[1].slice(0, maxDecimals) : parts[1])
          : '';
        sanitizedValue = parts[0] + decimalPart;
      }
      
      if (sanitizedValue === '' || (!isNaN(parseFloat(sanitizedValue)) && parseFloat(sanitizedValue) >= 0)) {
        onChange(sanitizedValue);
      }
    } else {
      onChange(inputValue.slice(0, maxLength));
    }
  };

  const handleKeyDown = (event) => {
    if (type === "number") {
      // Prevent 'e' from being entered
      if (event.key === 'e' || event.key === 'E') {
        event.preventDefault();
      }
      // Prevent decimal point if allowDecimal is false
      if (!allowDecimal && event.key === '.') {
        event.preventDefault();
      }
      // Prevent typing more decimals than allowed
      if (maxDecimals !== null && event.key !== 'Backspace' && event.key !== 'Delete') {
        const currentValue = String(value ?? '');
        const dotIndex = currentValue.indexOf('.');
        if (dotIndex !== -1 && currentValue.length - dotIndex - 1 >= maxDecimals) {
          // Already at max decimals — only allow if cursor is before the dot or user is selecting
          const input = event.target;
          const selStart = input.selectionStart;
          if (selStart > dotIndex && !/[0-9]/.test(event.key) === false) {
            event.preventDefault();
          }
        }
      }
    }
  };

  return (
    <TextField
      sx={{
        width: "100%",
        mt: mt,
        height: "32px",
        "& .MuiOutlinedInput-root": {
          height: "32px",
          backgroundColor,
          "& input": {
            fontFamily: "Lato",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "16px",
            padding: "8px",
          },
          "& fieldset": {
            borderColor: editBoolean ? "orange" : "",
            borderRadius: "6px",
          },
          "&:hover fieldset": {
            borderColor: editBoolean ? "orange" : "",
          },
          "&.Mui-focused fieldset": {
            borderColor: editBoolean ? "orange" : "",
          },
          "&.Mui-disabled": {
            opacity: 1,
            "& fieldset": {
              borderColor: editBoolean ? "orange" : "",
            },
          },
        },
        "& .MuiInputLabel-root": {
          fontFamily: "Lato",
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "16px",
          color: editBoolean ? "orange" : "#E0E2E7",
          "&.Mui-focused": {
            color: editBoolean ? "orange" : "#E0E2E7",
          },
          "&.Mui-disabled": {
            color: editBoolean ? "orange" : "#E0E2E7 !important",
          },
        },
        "& .MuiInputBase-input::placeholder": {
          fontFamily: "Lato",
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "16px",
        },
        "& .Mui-disabled": {
          "-webkit-text-fill-color": "#000000ba !important",
          backgroundColor: "transparent",
        },
        "& .MuiInputAdornment-root": {
          "& .MuiTypography-root": {
            fontFamily: "Lato",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "16px",
            color: "#B5BBC5",
          },
        },
      }}
      type={type === "number" ? "text" : type}
      value={value ?? ""}
      disabled={disable}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      label={label}
      placeholder={placeholder}
      size="small"
      helperText={errorMessage}
      error={!!errorMessage}
      onWheel={() => document.activeElement.blur()}
      InputProps={{
        endAdornment: maxLength && (
          <InputAdornment position="end">
            <span style={{ fontSize: '10px', color: '#B5BBC5', marginTop: '1px' }}>
              {`${value?.length ? value.length : 0}/${maxLength}`}
            </span>
          </InputAdornment>
        ),
      }}
      inputProps={{
        maxLength: maxLength,
      }}
    />
  );
};

export default MuiInput;